import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Table, Badge, Button, Alert, Spinner } from "react-bootstrap";
import { fetchLockedAccounts, unlockAccount } from "./DataService";

const roleLabel = (role) => {
  if (role === 1) return "Particulier";
  if (role === 2) return "Entreprise";
  if (role === 3) return "Livreur";
  return `Rôle ${role}`;
};

function LockedAccounts() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [unlocking, setUnlocking] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchLockedAccounts();
      setUsers(res.users ?? []);
    } catch {
      setError("Impossible de charger les comptes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleUnlock = async (user) => {
    setUnlocking(user.id);
    setError(null);
    setSuccess(null);
    try {
      await unlockAccount(user.id);
      setSuccess(`Compte ${user.email} déverrouillé.`);
      await load();
    } catch {
      setError("Erreur lors du déverrouillage.");
    } finally {
      setUnlocking(null);
    }
  };

  const locked = users.filter((u) => u.is_locked);
  const withAttempts = users.filter((u) => !u.is_locked && u.login_attempts > 0);

  return (
    <Container fluid className="py-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h4 className="mb-0">Comptes verrouillés</h4>
          <small className="text-muted">
            Verrou après 5 tentatives échouées — durée 15 min
          </small>
        </Col>
        <Col xs="auto">
          <Button variant="outline-secondary" size="sm" onClick={load} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : "↻ Rafraîchir"}
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <>
          {/* Comptes actuellement verrouillés */}
          <Card className="mb-4">
            <Card.Header className="d-flex align-items-center gap-2">
              <i className="bi bi-lock-fill text-danger" />
              <strong>Verrouillés maintenant</strong>
              {locked.length > 0 && (
                <Badge bg="danger" className="ms-1">{locked.length}</Badge>
              )}
            </Card.Header>
            <Card.Body className="p-0">
              {locked.length === 0 ? (
                <p className="text-muted text-center py-4 mb-0">Aucun compte verrouillé.</p>
              ) : (
                <Table hover responsive className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Nom</th>
                      <th>Email</th>
                      <th>Rôle</th>
                      <th>Tentatives</th>
                      <th>Déverrouillage dans</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {locked.map((u) => (
                      <tr key={u.id}>
                        <td>{u.nom}</td>
                        <td><small>{u.email}</small></td>
                        <td><Badge bg="secondary">{roleLabel(u.role)}</Badge></td>
                        <td>
                          <Badge bg="danger">{u.login_attempts} tentative{u.login_attempts > 1 ? "s" : ""}</Badge>
                        </td>
                        <td>
                          <span className="text-danger fw-semibold">
                            {u.remaining_min !== null ? `${u.remaining_min} min` : "—"}
                          </span>
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-success"
                            onClick={() => handleUnlock(u)}
                            disabled={unlocking === u.id}
                          >
                            {unlocking === u.id ? <Spinner animation="border" size="sm" /> : "Débloquer"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          {/* Comptes avec tentatives échouées (verrou expiré) */}
          {withAttempts.length > 0 && (
            <Card>
              <Card.Header className="d-flex align-items-center gap-2">
                <i className="bi bi-exclamation-triangle-fill text-warning" />
                <strong>Tentatives échouées récentes</strong>
                <Badge bg="warning" text="dark" className="ms-1">{withAttempts.length}</Badge>
              </Card.Header>
              <Card.Body className="p-0">
                <Table hover responsive className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Nom</th>
                      <th>Email</th>
                      <th>Rôle</th>
                      <th>Tentatives</th>
                      <th>Dernière connexion</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {withAttempts.map((u) => (
                      <tr key={u.id}>
                        <td>{u.nom}</td>
                        <td><small>{u.email}</small></td>
                        <td><Badge bg="secondary">{roleLabel(u.role)}</Badge></td>
                        <td>
                          <Badge bg="warning" text="dark">
                            {u.login_attempts} tentative{u.login_attempts > 1 ? "s" : ""}
                          </Badge>
                        </td>
                        <td>
                          <small className="text-muted">
                            {u.last_login_at
                              ? new Date(u.last_login_at).toLocaleString("fr-FR", {
                                  day: "2-digit", month: "2-digit", year: "numeric",
                                  hour: "2-digit", minute: "2-digit",
                                })
                              : "—"}
                          </small>
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => handleUnlock(u)}
                            disabled={unlocking === u.id}
                          >
                            {unlocking === u.id ? <Spinner animation="border" size="sm" /> : "Réinitialiser"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </Container>
  );
}

export default LockedAccounts;
